import { Check, CircleX, Loader, Circle, Hourglass } from "lucide-react";
import { useState } from "react";

const STATUS_CONFIG = {
  approved: { icon: <Check size={13} strokeWidth={2.5} /> },
  rejected: { icon: <CircleX size={13} strokeWidth={2} /> },
  pending:  { icon: <Hourglass size={13} strokeWidth={2} /> },
  active:   { icon: <Loader size={13} strokeWidth={2} /> },
  future:   { icon: <Circle size={13} strokeWidth={2} /> },
  inactive: { icon: <Circle size={13} strokeWidth={2} /> },
  completed: { icon: <Check size={13} strokeWidth={2.5} /> },
  custom1: { icon: <CircleX size={13} strokeWidth={2} /> },
  custom2: { icon: <Check size={13} strokeWidth={2.5} /> },
};

const nodeStyles = {
  approved: {
    card:  { background: "#f0faf4", border: "1.5px solid #6bbf8e" },
    title: { color: "#1a3d2b" },
    icon:  { background: "#4caf72", color: "#fff" },
  },
  rejected: {
    card:  { background: "#fff5f5", border: "1.5px solid #e57373" },
    title: { color: "#5c1a1a" },
    icon:  { background: "#e53935", color: "#fff" },
  },
  pending: {
    card:  { background: "#fffbf0", border: "1.5px solid #f5c842" },
    title: { color: "#4a3800" },
    icon:  { background: "#f5c842", color: "#fff" },
  },
  active: {
    card:  { background: "#f0f6ff", border: "1.5px solid #5b8dee" },
    title: { color: "#1a2d5a" },
    icon:  { background: "#5b8dee", color: "#fff" },
  },
  future: {
    card:  { background: "#f6f6f6", border: "1.5px solid #686c70" },
    title: { color: "#686c70" },
    icon:  { background: "#686c70", color: "#fff" },
  },
  inactive: {
    card:  { background: "#f8f9fa", border: "1.5px solid #686C70" },
    title: { color: "#686C70" },
    icon:  { background: "#686C70", color: "#fff" },
  },
  completed: {
    card:  { background: "#f0faf4", border: "1.5px solid #327E45" },
    title: { color: "#327E45" },
    icon:  { background: "#327E45", color: "#fff" },
  },
  custom1: {
    card:  { background: "#fff5f5", border: "1.5px solid #E84E4E" },
    title: { color: "#E84E4E" },
    icon:  { background: "#E84E4E", color: "#fff" },
  },
  custom2: {
    card:  { background: "#fff8f0", border: "1.5px solid #F49F28" },
    title: { color: "#F49F28" },
    icon:  { background: "#F49F28", color: "#fff" },
  },
};

/**
 * Props:
 *   title      {string}  — e.g. "Application Received"
 *   date       {string}  — e.g. "Apr 24, 2024"
 *   status     {string}  — "approved" | "rejected" | "pending" | "active"
 *   tat        {string?} — optional TAT label, e.g. "TAT 2 hours"
 *   connector  {bool?}   — show bottom connector line (default false)
 */
export default function StageNode({ title, date, status = "approved", tat, connector = false, processedBy, active = false, disableHover = false }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.approved;
  const ns  = nodeStyles[status] || nodeStyles.approved;

  const [isHovered, setIsHovered] = useState(false);

  // Override styles when active (panels are open)
  const activeStyles = active ? {
    card: { background: "#e3f2fd", border: "1.5px solid #2196f3" },
    title: { color: "#1565c0" },
    icon: { background: "#2196f3", color: "#fff" },
  } : {};

  // Hover styles: blue for normal hover
  const hoverStyles = isHovered ? {
    card: { background: "#e3f2fd", border: "1.5px solid #2196f3" },
    title: { color: "#1565c0" },
    icon: { background: "#2196f3", color: "#fff" },
  } : {};

  // Special green styles for disabled hover (Application Received)
  const disabledHoverStyles = disableHover ? {
    card: { background: "#E4F1E8", border: "1.5px solid #327E45" },
    title: { color: "#327E45" },
    icon: { background: "#327E45", color: "#fff" },
  } : {};

  const finalStyles = active ? activeStyles : (disableHover ? disabledHoverStyles : (isHovered ? hoverStyles : ns));

  return (
    <div 
      onMouseEnter={disableHover ? undefined : () => setIsHovered(true)}
      onMouseLeave={disableHover ? undefined : () => setIsHovered(false)}
      style={{
        width: "350px",
        borderRadius: "10px",
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        position: "relative",
        zIndex: 1,
        cursor: disableHover ? "default" : "pointer",
        ...finalStyles.card,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <span style={{ fontSize: "16px", fontWeight: 700, lineHeight: 1.2, color: "#0C0B10DE" }}>{title}</span>
        <span style={{
          width: "24px", height: "24px", borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, ...finalStyles.icon,
        }}>
          {cfg.icon}
        </span>
      </div>
      <div style={{ marginTop: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: "14px", color: "#686C70", fontWeight: 400 }}>{processedBy}</span>
        <span style={{ fontSize: "14px", color: "#686C70", fontWeight: 400 }}>{date}</span>
      </div>
      {tat && (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ padding: "4px 10px", borderRadius: "20px", background: "#fef3e2", color: "#b45309", fontSize: "12px", fontWeight: 500, width: "fit-content" }}>{tat}</span>
          <div style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: "#a0aab4",
            flexShrink: 0
          }} />
        </div>
      )}
    </div>
  );
}
